import { useEffect, useState, useRef } from "react";
import { posChannel } from "./posChannel";
import { Cart } from "../types";
import { getDecryptedToken } from "../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../utils/currency/currencyUtils";
import Promotions from "../../customerview/Promotions";
import { getMediaUrl } from "../../../utils/mediaUrl";

export default function CustomerView() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [now, setNow] = useState<Date>(new Date());
  const [isAnimating, setIsAnimating] = useState(false);
  const previousCartRef = useRef<Cart | null>(null);
  const itemCountRef = useRef(0);
  const listEndRef = useRef<HTMLDivElement | null>(null);

  const shopName =
    getDecryptedToken("martName") || getDecryptedToken("name") || "Shop";
  const address =
    getDecryptedToken("address") || getDecryptedToken("martAddress") || "";
  const phoneNumber =
    getDecryptedToken("phoneNumber") ||
    getDecryptedToken("martPhone") ||
    getDecryptedToken("phone") ||
    "";
  const ownerName = getDecryptedToken("name") || "";
  const email = getDecryptedToken("email") || "";
  const currency = getDecryptedToken("currency") || "";
  const currencySymbol = getCurrencySymbol() || currency;

  const calculateTotals = () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { total: 0, discount: 0, tax: 0, netTotal: 0, itemCount: 0 };
    }

    const total = cart.items.reduce((sum, item) => sum + item.total, 0);
    const netTotal = cart.grandTotal;
    const discount = Math.max(0, total - netTotal);
    const tax = 0;
    const itemCount = cart.items.reduce((sum, item) => sum + item.qty, 0);

    return { total, discount, tax, netTotal, itemCount };
  };

  const totals = calculateTotals();

  useEffect(() => {
    if (cart && previousCartRef.current) {
      const prevCount = previousCartRef.current.items?.length || 0;
      const newCount = cart.items?.length || 0;

      if (newCount !== prevCount) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 500);
      }
    }
    previousCartRef.current = cart;
    itemCountRef.current = totals.itemCount;
  }, [cart, totals.itemCount]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [cart?.items?.length]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const requestFullscreen = async () => {
    const isFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    if (isFullscreen) return;

    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        await (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).mozRequestFullScreen) {
        await (document.documentElement as any).mozRequestFullScreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        await (document.documentElement as any).msRequestFullscreen();
      }
    } catch {
      // Fullscreen request may be blocked by the browser
    }
  };

  useEffect(() => {
    const handleMessage = (e: MessageEvent<Cart>) => {
      setCart(e.data);
    };

    posChannel.onmessage = handleMessage;

    const handleWindowMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === "REQUEST_FULLSCREEN") {
        requestFullscreen();
      }
    };

    window.addEventListener("message", handleWindowMessage);

    return () => {
      posChannel.onmessage = null;
      window.removeEventListener("message", handleWindowMessage);
    };
  }, []);

  useEffect(() => {
    const setupAutoFullscreen = () => {
      const handleAnyInteraction = () => requestFullscreen();

      document.addEventListener("click", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("touchstart", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("mousedown", handleAnyInteraction, {
        once: true,
        capture: true,
      });
      document.addEventListener("keydown", handleAnyInteraction, {
        once: true,
        capture: true,
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => requestFullscreen());
      });

      setTimeout(() => requestFullscreen(), 0);
      setTimeout(() => requestFullscreen(), 100);
      setTimeout(() => requestFullscreen(), 300);
      setTimeout(() => requestFullscreen(), 500);
    };

    const setupFullScreenStyles = () => {
      document.documentElement.style.width = "100%";
      document.documentElement.style.height = "100%";
      document.documentElement.style.margin = "0";
      document.documentElement.style.padding = "0";
      document.documentElement.style.overflow = "hidden";

      if (document.body) {
        document.body.style.width = "100%";
        document.body.style.height = "100%";
        document.body.style.margin = "0";
        document.body.style.padding = "0";
        document.body.style.overflow = "hidden";
      }
    };

    setupFullScreenStyles();
    setupAutoFullscreen();

    if (document.readyState === "complete") {
      setupAutoFullscreen();
    } else {
      window.addEventListener("load", setupAutoFullscreen);
      document.addEventListener("DOMContentLoaded", setupAutoFullscreen);
    }

    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      if (!isFullscreen) {
        setTimeout(() => requestFullscreen(), 100);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
      document.removeEventListener("load", setupAutoFullscreen);
      document.removeEventListener("DOMContentLoaded", setupAutoFullscreen);
    };
  }, []);

  const formatMoney = (value: number) =>
    `${currencySymbol} ${value.toFixed(2)}`;

  const clockLabel = now.toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // Idle / welcome — promo is the full visual plane
  if (!cart || cart.items.length === 0) {
    return (
      <div
        className="relative h-screen w-full cursor-pointer overflow-hidden bg-erp-navy font-inter"
        onClick={() => requestFullscreen()}
        onTouchStart={() => requestFullscreen()}
      >
        <style>{`
          @keyframes cvWelcomeIn {
            from { opacity: 0; transform: translateY(18px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes cvGlow {
            0%, 100% { opacity: 0.45; }
            50% { opacity: 0.85; }
          }
        `}</style>

        <div className="absolute inset-0 z-0">
          <Promotions />
        </div>

        <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(11,28,44,0.15)_0%,transparent_28%,transparent_62%,rgba(11,28,44,0.88)_100%)]" />
        <div className="pointer-events-none absolute -left-24 top-16 z-[1] h-56 w-56 rounded-full bg-erp-gold/20 blur-3xl animate-[cvGlow_5s_ease-in-out_infinite]" />
        <div className="pointer-events-none absolute -right-16 bottom-40 z-[1] h-64 w-64 rounded-full bg-erp-accent/25 blur-3xl animate-[cvGlow_6s_ease-in-out_infinite]" />

        <div className="absolute inset-x-0 bottom-0 z-[2] px-8 pb-8 pt-16 animate-[cvWelcomeIn_0.9s_cubic-bezier(0.22,1,0.36,1)]">
          <div className="mx-auto flex max-w-6xl flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="m-0 mb-2 text-[12px] font-bold uppercase tracking-[0.22em] text-erp-gold">
                Customer Display
              </p>
              <h1 className="m-0 font-brand text-[clamp(2.8rem,7vw,5rem)] font-bold leading-[0.95] tracking-tight text-white">
                Welcome!
              </h1>
              <p className="m-0 mt-3 text-[clamp(1.1rem,2.2vw,1.5rem)] font-medium text-white/85">
                Thank you for visiting{" "}
                <span className="font-bold text-erp-gold">{shopName}</span>
              </p>
              <p className="m-0 mt-2 max-w-xl text-[clamp(0.95rem,1.6vw,1.15rem)] font-medium italic text-white/55">
                Your order will appear here once items are added
              </p>
              {(address || phoneNumber || email) && (
                <div className="mt-4 space-y-1 text-[13px] font-medium text-white/60">
                  {address && (
                    <p className="m-0 whitespace-pre-line">{address}</p>
                  )}
                  {phoneNumber && <p className="m-0">{phoneNumber}</p>}
                  {email && <p className="m-0">{email}</p>}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-[13px] font-semibold tabular-nums text-white/80 backdrop-blur-md">
              {clockLabel}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative h-screen w-full cursor-pointer overflow-hidden bg-[#f4f7fb] font-inter"
      onClick={() => requestFullscreen()}
      onTouchStart={() => requestFullscreen()}
    >
      <style>{`
        @keyframes cvSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cvPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes cvFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cv-row { animation: cvSlide 0.32s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .cv-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(201,162,77,0.7) transparent;
        }
        .cv-scroll::-webkit-scrollbar { width: 6px; }
        .cv-scroll::-webkit-scrollbar-track { background: transparent; }
        .cv-scroll::-webkit-scrollbar-thumb {
          background: rgba(201,162,77,0.7);
          border-radius: 999px;
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_0%_0%,rgba(31,111,139,0.07),transparent_42%),radial-gradient(ellipse_at_100%_100%,rgba(201,162,77,0.1),transparent_48%)]" />

      <div className="relative flex h-full w-full gap-5 p-5">
        {/* Order column */}
        <section className="flex min-h-0 min-w-0 flex-[1.15] flex-col overflow-hidden rounded-2xl border border-erp-line bg-white shadow-[0_10px_36px_rgba(11,28,44,0.07)]">
          {/* Header aligned to items table */}
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-erp-line px-5 py-4">
            <div className="min-w-0">
              <p className="m-0 text-[12px] font-bold uppercase tracking-[0.14em] text-erp-gold">
                Live Order
              </p>
              <h1 className="m-0 mt-1 font-brand text-[clamp(1.9rem,3vw,2.6rem)] font-bold leading-none tracking-tight text-erp-navy">
                Your Order
              </h1>
            </div>

            <div
              className={`shrink-0 text-right leading-none ${
                isAnimating ? "animate-[cvPulse_0.55s_ease-in-out]" : ""
              }`}
            >
              <div className="font-brand text-[clamp(1.15rem,2vw,1.45rem)] font-bold uppercase tracking-[0.14em] text-erp-navy">
                <span className="text-erp-accent">{cart.items.length}</span>
                <span className="ml-1.5">
                  {cart.items.length === 1 ? "Line" : "Lines"}
                </span>
                <span className="mx-3 inline-block h-1.5 w-1.5 rounded-full bg-erp-gold align-middle" />
                <span className="text-erp-accent">{totals.itemCount}</span>
                <span className="ml-1.5">
                  {totals.itemCount === 1 ? "Item" : "Items"}
                </span>
              </div>
            </div>
          </header>

          <div className="cv-scroll min-h-0 flex-1 overflow-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft">
                  <th className="w-16 px-4 py-3.5 text-center text-[12px] font-bold uppercase tracking-[0.08em] text-white/90">
                    No
                  </th>
                  <th className="px-5 py-3.5 text-left text-[12px] font-bold uppercase tracking-[0.08em] text-white/90">
                    Item
                  </th>
                  <th className="px-5 py-3.5 text-right text-[12px] font-bold uppercase tracking-[0.08em] text-white/90">
                    Price
                  </th>
                  <th className="px-5 py-3.5 text-center text-[12px] font-bold uppercase tracking-[0.08em] text-white/90">
                    Qty
                  </th>
                  <th className="px-5 py-3.5 text-right text-[12px] font-bold uppercase tracking-[0.08em] text-white/90">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {cart.items.map((item, index) => (
                  <tr
                    key={item.id}
                    className="cv-row border-b border-erp-line/70 transition-colors hover:bg-erp-surface/80"
                    style={{ animationDelay: `${index * 0.035}s` }}
                  >
                    <td className="px-4 py-3.5 text-center text-[15px] font-bold tabular-nums text-erp-muted">
                      {index + 1}
                    </td>
                    <td className="px-5 py-3.5 text-left">
                      <div className="flex items-center gap-3">
                       
                        <span className="text-[18px] font-semibold leading-snug text-erp-ink">
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-right text-[16px] font-semibold tabular-nums text-erp-ink">
                      {formatMoney(item.total / item.qty)}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="inline-flex min-w-[48px] items-center justify-center rounded-lg bg-erp-navy px-3 py-1.5 text-[15px] font-bold text-white">
                        {item.qty}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right text-[17px] font-bold tabular-nums text-erp-navy">
                      {formatMoney(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={listEndRef} />
          </div>

            {/* Totals dock */}
            <div className="shrink-0 border-t border-erp-line bg-[linear-gradient(180deg,#ffffff_0%,#f7fafc_100%)] px-5 py-4 animate-[cvFade_0.45s_ease-out]">
              <div className="mb-3 space-y-1.5">
                <div className="flex items-center justify-between text-[14px] text-erp-muted">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-semibold tabular-nums text-erp-ink">
                    {formatMoney(totals.total)}
                  </span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex items-center justify-between text-[14px] text-erp-success">
                    <span className="font-medium">Discount</span>
                    <span className="font-semibold tabular-nums">
                      − {formatMoney(totals.discount)}
                    </span>
                  </div>
                )}
                {totals.tax > 0 && (
                  <div className="flex items-center justify-between text-[14px] text-erp-muted">
                    <span className="font-medium">Tax</span>
                    <span className="font-semibold tabular-nums text-erp-ink">
                      {formatMoney(totals.tax)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4 shadow-[0_10px_28px_rgba(11,28,44,0.22)]">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-erp-gold/90">
                    Total Amount
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-white/55">
                    Inclusive of applied discounts
                  </div>
                </div>
                <div className="font-brand text-[clamp(1.75rem,3vw,2.35rem)] font-bold leading-none tabular-nums text-erp-gold">
                  {formatMoney(totals.netTotal)}
                </div>
              </div>
            </div>
          </section>

          {/* Shop details + promotions */}
          <aside className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-erp-line bg-white shadow-[0_10px_36px_rgba(11,28,44,0.08)]">
            {/* Shop header — all previous identity data */}
            <div className="flex shrink-0 items-center gap-4 border-b border-erp-line px-5 py-4">
              <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl border border-erp-gold/40 bg-gradient-to-br from-erp-navy via-erp-navy-mid to-erp-navy-soft text-[1.5rem] font-bold text-erp-gold shadow-[0_8px_20px_rgba(11,28,44,0.2)]">
                {shopName?.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[1.4rem] font-bold tracking-tight text-erp-ink">
                  {shopName}
                </div>
                {ownerName && (
                  <div className="mt-1 text-[13px] font-medium text-erp-muted">
                    Cashier: {ownerName}
                  </div>
                )}
                {currency && (
                  <div className="mt-0.5 text-[12px] font-semibold text-erp-accent">
                    Currency: {currencySymbol || currency}
                  </div>
                )}
              </div>

              {/* put date time end of here
               */}
              <div className="text-[20px] font-semibold text-erp-accent">
                {clockLabel}
              </div>
            </div>

            {/* Shop contact details — shown once here */}
            <div className="shrink-0 space-y-2 border-b border-erp-line px-5 py-4">
              {address && (
                <div className="whitespace-pre-line rounded-xl border border-erp-line bg-erp-surface px-3.5 py-3 text-[14px] leading-relaxed text-erp-ink">
                  {address}
                </div>
              )}
              {phoneNumber && (
                <div className="rounded-xl border border-erp-line bg-erp-surface px-3.5 py-2.5 text-[14px] font-medium text-erp-ink">
                  {phoneNumber}
                </div>
              )}
              {email && (
                <div className="rounded-xl border border-erp-line bg-erp-surface px-3.5 py-2.5 text-[14px] font-medium text-erp-ink">
                  {email}
                </div>
              )}
            </div>

            {/* Promotions */}
            <div className="relative min-h-0 flex-1 bg-erp-surface">
              <Promotions isInline={true} />
            </div>

            <div className="shrink-0 border-t border-dashed border-erp-line px-5 py-3 text-center text-[13px] font-medium text-erp-muted">
              <a
                href="https://visolabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-erp-accent no-underline transition-colors hover:text-erp-navy"
              >
                visolabs.io
              </a>
            </div>
          </aside>
      </div>
    </div>
  );
}
